import { Random } from '../utils/Random.js';

/**
 * 拼图边缘生成器
 * 生成拼图块之间的凹凸边缘数据
 */
export class PuzzleGenerator {
    constructor(config = {}) {
        this.config = {
            tabSize: config.tabSize ?? 0.1,
            jitter: config.jitter ?? 0.04,
            seed: config.seed ?? 1234
        };

        this.random = new Random(this.config.seed);

        // 存储边缘数据
        // horizontalEdges[row][col] = 第row行下方、第col列的水平边缘
        // verticalEdges[row][col] = 第row行、第col列右侧的垂直边缘
        this.horizontalEdges = [];
        this.verticalEdges = [];

        this.cols = 0;
        this.rows = 0;
    }

    /**
     * 生成所有边缘数据
     * @param {number} cols 列数
     * @param {number} rows 行数
     */
    generate(cols, rows) {
        this.cols = cols;
        this.rows = rows;
        this.random.reset();

        this.generateHorizontalEdges();
        this.generateVerticalEdges();

        return {
            horizontalEdges: this.horizontalEdges,
            verticalEdges: this.verticalEdges,
            cols,
            rows
        };
    }

    /**
     * 生成水平边缘数据
     */
    generateHorizontalEdges() {
        this.horizontalEdges = [];

        // rows-1 条内部水平线
        for (let row = 0; row < this.rows - 1; row++) {
            this.horizontalEdges[row] = [];
            for (let col = 0; col < this.cols; col++) {
                this.horizontalEdges[row][col] = this.generateEdgeData();
            }
        }
    }

    /**
     * 生成垂直边缘数据
     */
    generateVerticalEdges() {
        this.verticalEdges = [];

        // cols-1 条内部垂直线
        for (let row = 0; row < this.rows; row++) {
            this.verticalEdges[row] = [];
            for (let col = 0; col < this.cols - 1; col++) {
                this.verticalEdges[row][col] = this.generateEdgeData();
            }
        }
    }

    /**
     * 生成单条边缘的随机参数
     * @returns {Object} 边缘数据
     */
    generateEdgeData() {
        const { jitter } = this.config;
        return {
            flip: this.random.bool(),                    // 凸起方向
            a: this.random.range(-jitter, jitter),       // 起始控制点偏移
            b: this.random.range(-jitter, jitter),       // 凸起位置偏移
            c: this.random.range(-jitter, jitter),       // 凸起高度偏移
            d: this.random.range(-jitter, jitter),       // 凸起宽度偏移
            e: this.random.range(-jitter, jitter)        // 结束控制点偏移
        };
    }

    /**
     * 获取指定位置的边缘数据
     * @param {number} col 列
     * @param {number} row 行
     * @returns {Object} 四条边的边缘数据
     */
    getEdgesForPiece(col, row) {
        return {
            top: row > 0 ? this.horizontalEdges[row - 1][col] : null,
            right: col < this.cols - 1 ? this.verticalEdges[row][col] : null,
            bottom: row < this.rows - 1 ? this.horizontalEdges[row][col] : null,
            left: col > 0 ? this.verticalEdges[row][col - 1] : null
        };
    }

    /**
     * 生成单条边的贝塞尔曲线路径
     * @param {number} startX 起始X坐标
     * @param {number} startY 起始Y坐标
     * @param {number} size 边长
     * @param {string} direction 方向: 'right', 'down', 'left', 'up'
     * @param {Object} edge 边缘数据，null表示边界直线
     * @param {boolean} isInverted 是否反转凹凸方向（同时需要反转控制点顺序）
     * @returns {string} SVG路径字符串
     */
    generateEdgeCurve(startX, startY, size, direction, edge, isInverted = false) {
        if (!edge) {
            // 边界直线
            switch (direction) {
                case 'right': return `L ${startX + size} ${startY}`;
                case 'down': return `L ${startX} ${startY + size}`;
                case 'left': return `L ${startX - size} ${startY}`;
                case 'up': return `L ${startX} ${startY - size}`;
            }
        }

        const { flip, a, b, c, d, e } = edge;
        const t = this.config.tabSize;

        // 决定凸起方向
        const actualFlip = isInverted ? !flip : flip;
        const sign = actualFlip ? 1 : -1;

        // 当 isInverted 时，需要从相反方向绘制
        // 想象从边缘的终点往起点看，所有沿边缘方向的偏移都需要取反
        // a/e 需要交换（起点变终点）
        // b 需要取反（凸起位置相对于中心的偏移方向反转）
        // d 需要取反（宽度偏移方向反转）
        const actualA = isInverted ? e : a;
        const actualE = isInverted ? a : e;
        const actualB = isInverted ? -b : b;
        const actualD = isInverted ? -d : d;

        // 控制点（沿边缘方向的比例, 垂直于边缘的偏移）
        const pts = [
            [0.2, actualA * sign],
            [0.5 + actualB + actualD, (-t + c) * sign],
            [0.5 - t + actualB, (t + c) * sign],
            [0.5 - 2 * t + actualB - actualD, (3 * t + c) * sign],
            [0.5 + 2 * t + actualB - actualD, (3 * t + c) * sign],
            [0.5 + t + actualB, (t + c) * sign],
            [0.5 + actualB + actualD, (-t + c) * sign],
            [0.8, actualE * sign],
            [1.0, 0]
        ];

        // 根据方向转换坐标
        const transform = (along, perp) => {
            switch (direction) {
                case 'right': return [startX + along * size, startY + perp * size];
                case 'down': return [startX - perp * size, startY + along * size];
                case 'left': return [startX - along * size, startY - perp * size];
                case 'up': return [startX + perp * size, startY - along * size];
            }
        };

        let path = '';

        // 第一段贝塞尔曲线
        const [x1, y1] = transform(pts[0][0], pts[0][1]);
        const [x2, y2] = transform(pts[1][0], pts[1][1]);
        const [x3, y3] = transform(pts[2][0], pts[2][1]);
        path += `C ${x1} ${y1} ${x2} ${y2} ${x3} ${y3} `;

        // 第二段贝塞尔曲线
        const [x4, y4] = transform(pts[3][0], pts[3][1]);
        const [x5, y5] = transform(pts[4][0], pts[4][1]);
        const [x6, y6] = transform(pts[5][0], pts[5][1]);
        path += `C ${x4} ${y4} ${x5} ${y5} ${x6} ${y6} `;

        // 第三段贝塞尔曲线
        const [x7, y7] = transform(pts[6][0], pts[6][1]);
        const [x8, y8] = transform(pts[7][0], pts[7][1]);
        const [x9, y9] = transform(pts[8][0], pts[8][1]);
        path += `C ${x7} ${y7} ${x8} ${y8} ${x9} ${y9} `;

        return path;
    }

    /**
     * 生成完整拼图块的路径
     * @param {number} col 列位置
     * @param {number} row 行位置
     * @param {number} size 块大小
     * @returns {Object} 包含path和padding的对象
     */
    generatePiecePath(col, row, size) {
        const padding = size * this.config.tabSize * 4;
        const originX = padding;
        const originY = padding;

        const edges = this.getEdgesForPiece(col, row);

        let path = `M ${originX} ${originY} `;

        // 上边缘
        path += this.generateEdgeCurve(originX, originY, size, 'right', edges.top, row > 0);

        // 右边缘
        path += this.generateEdgeCurve(originX + size, originY, size, 'down', edges.right, false);

        // 下边缘
        path += this.generateEdgeCurve(originX + size, originY + size, size, 'left', edges.bottom, false);

        // 左边缘
        path += this.generateEdgeCurve(originX, originY + size, size, 'up', edges.left, col > 0);

        path += 'Z';

        return { path, padding };
    }

    /**
     * 更新配置
     * @param {Object} config 新配置
     */
    updateConfig(config) {
        this.config = { ...this.config, ...config };
        if (config.seed !== undefined) {
            this.random.setSeed(config.seed);
        }
    }

    /**
     * 获取tabSize（用于外部计算）
     * @returns {number} tabSize值
     */
    getTabSize() {
        return this.config.tabSize;
    }
}

export default PuzzleGenerator;
